import { ServiceBase } from "@app/shared";
import { updateFieldNames } from "@app/shared/utility";
import { AccuracySpecialtyDefinition, AccuracySpecialtyDefinitionMetadata } from "..";
import { AccuracyTemplate, AccuracyTemplateCollectionMetadata } from "./models";

/** Manages interaction with the template set API. */
export default class AccuracyTemplateService extends ServiceBase {
  /**
   * Returns metadata about all template collections and associated templates for the current user.
   * Does not include the actual template definition, just information that might be useful for the UI - name, description, etc.
   */
  public async getTemplateCollections(): Promise<AccuracyTemplateCollectionMetadata[]> {
    return await this.makeGet<AccuracyTemplateCollectionMetadata[]>("accuracy-templates");
  }

  /**
   * Retrieves the full template (its "details") with all inspection information attached.
   * @param templateCollectionId The collection to retrieve from.
   * @param templateId The template to specifically retrieve.
   */
  public async getTemplateDetails(templateCollectionId: string, templateId: string): Promise<AccuracyTemplate> {
    let template = await this.makeGet<AccuracyTemplate>(`accuracy-templates/${encodeURIComponent(templateCollectionId)}/template/${encodeURIComponent(templateId)}/details`);

    // UID comes across all capitalized, make it so it aligns with our interface + the rest of our code.
    if (template) {
      template = updateFieldNames(template, /UID/, "uid");
    }

    return template;
  }

  public async getSpecialtyDefinitions(): Promise<AccuracySpecialtyDefinition[]> {

    const metadata = await this.makeGet<AccuracySpecialtyDefinitionMetadata[]>("specialty-definitions");

    const specialtyDefs: AccuracySpecialtyDefinition[] = [];

    for (const meta of metadata) {
      const specialtyDefinition: AccuracySpecialtyDefinition = {
        categories: null,
        name: meta.resource.name,
        specialtyDefinitionId: meta.specialtyDefinitionId,
      };

      specialtyDefs.push(specialtyDefinition);
    }

    return specialtyDefs;
  }

  public async getSpecialtyDefinitionById(specialtyDefinitionId: string): Promise<AccuracySpecialtyDefinition> {
    const result = await this.makeGet<AccuracySpecialtyDefinition>(`specialty-definitions/${specialtyDefinitionId}`);

    if (result) {
      const specialtyDefinition: AccuracySpecialtyDefinition = {
        categories: result.categories,
        name: null,
        specialtyDefinitionId: null,
      };

      return specialtyDefinition;
    }

    return result;
  }
}
